import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  output,
  Output,
  viewChild
} from '@angular/core';
import type { JuiImageClick, JuiImageEntry, JuiImageRejection } from '@jump-tech-frontend/core-components/components';
import {
  JuiAlertBlock,
  JuiButton,
  JuiImageCapture,
  JuiSkeletonLoader
} from '@jump-tech-frontend/core-components-angular';
import { MetadataPanelComponent } from '@jump-tech-frontend/image-analysis';
import { QuestionExampleImagesComponent } from '../../components/question-example-images.component';
import { FormUpdate, IQuestionComponent } from '../question.model';
import { ImageCaptureQuestionParams } from './image-capture.model';
import { ImageCapturePresenter } from './image-capture.presenter';

@Component({
  selector: 'question-image-capture',
  templateUrl: './image-capture.component.html',
  styleUrls: ['./image-capture.component.scss'],
  imports: [
    JuiImageCapture,
    JuiAlertBlock,
    JuiButton,
    JuiSkeletonLoader,
    MetadataPanelComponent,
    QuestionExampleImagesComponent
  ],
  providers: [ImageCapturePresenter],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageCaptureComponent implements IQuestionComponent {
  private readonly presenter = inject(ImageCapturePresenter);
  private readonly control = viewChild.required(JuiImageCapture);

  public readonly params = input.required<ImageCaptureQuestionParams>();
  @Output() formChange: EventEmitter<FormUpdate> = new EventEmitter<FormUpdate>();
  public readonly imageOpen = output<number>();
  public readonly analysingChange = output<boolean>();

  public readonly vm = this.presenter.vm;

  constructor() {
    this.presenter.onValueChange(value => this.formChange.emit({ key: this.params().key, value }));
    effect(() => this.presenter.load(this.params()));
    effect(() => this.analysingChange.emit(this.presenter.analysing()));
  }

  public onImagesChange(event: CustomEvent<JuiImageEntry[]>): void {
    this.presenter.imagesChanged(event.detail);
  }

  public onReject(event: CustomEvent<JuiImageRejection>): void {
    this.presenter.rejected(event.detail);
  }

  public onImageClick(event: CustomEvent<JuiImageClick>): void {
    if (this.vm().readonly) {
      this.imageOpen.emit(event.detail.index);
      return;
    }
    this.presenter.toggleBoxes(event.detail.id);
  }

  public toggleBoxes(id: string): void {
    this.presenter.toggleBoxes(id);
  }

  public onCaptureRequest(event: CustomEvent<void>): void {
    if (!this.presenter.offersCamera()) {
      return;
    }
    event.preventDefault();
    void this.captureWithCamera();
  }

  private async captureWithCamera(): Promise<void> {
    const photo = await this.presenter.captureWithCamera(() => void this.control().openPicker());
    if (photo) {
      await this.control().addImages([photo]);
    }
  }
}
